import { useState, useEffect } from 'react';
import format from 'date-fns/format';
import parse from 'date-fns/parse';
import localeEs from 'date-fns/locale/es';
import CloudDownloadIcon from '@material-ui/icons/CloudDownload';
import { Button, Grid, IconButton, CircularProgress, TextField, Tooltip, Typography } from '@material-ui/core';
import { MUIDataTableColumnDef, MUIDataTableMeta, MUIDataTableOptions, MUIDataTableState } from 'mui-datatables';
import Chip from '@material-ui/core/Chip';
import MuiTable from 'components/molecules/MuiTable';
import AdminTemplate from 'components/templates/AdminTemplate';
import useOrders from 'hooks/useOrders';
import textLabels from 'utils/MUIDataTableTextLabels';
import { useOrdersContext } from 'contextApi/OrdersContext';
import CreateShippingModal from 'components/organisms/CreateShippingModal';
import useShippingCarrier from 'hooks/useShippingCarrier';
import useOrderPackages from 'hooks/useOrderPackages';
import { PaginationOrders } from 'interfaces/pagination.interface';
import { IGeneratePdf, IOrderId, IOrderToShipping, OrderProducts, TrackingDetails } from 'interfaces/order.interface';
import { IOrderProducts } from 'interfaces/orderPackages.interface';
import CustomToolBarOrders from 'components/molecules/CustomToolBarOrders';
import { differenceInDays } from 'date-fns';

export default function Orders() {
  const [isLoading, setIsLoading] = useState(false);
  const [orders, setOrders] = useState<any>([]); // order.IOrder[]
  const [totalOrders, setTotalOrders] = useState(0);
  const [pagination, setPagination] = useState<PaginationOrders>({
    limit: 10,
    page: 1,
    orderBy: 'status',
    order: 'desc',
    search: ''
  });
  const [labelId, setLabelId] = useState<string>('55');
  const [validateField, setValidateField] = useState({
    haveErrors: false,
    messageError: ''
  });
  const [generatePdfInfo, setGeneratePdfInfo] = useState<IGeneratePdf>({
    labelId: '',
    ordersToPdf: []
  });
  const { getOrders, getOrderByOrderNumber } = useOrders();
  const { setOrderToShipping, setOrderPackages, setOrderPackageShipping, orderPackageShipping } = useOrdersContext();
  const { generatePdfOfShippings } = useShippingCarrier();
  const { getOrderPackages } = useOrderPackages();

  const createShipping = (orderNumber: number) => {
    setIsLoading(true);
    getOrderByOrderNumber(orderNumber).then((order) => {
      const orderToShipping = {
        orderId: order.orderNumber,
        orderNumber: order.orderNumber,
        recipientsName: `${order.shipping.first_name} ${order.shipping.last_name}`,
        recipientsProvinceName: order.shipping.state,
        recipientsCityName: order.shipping.city,
        packages: order.products, // cambiar a package
        recipientsAddress: `${order.shipping.address_1} ${order.shipping.address_2}`,
        name: `#${order.orderNumber.toString()}`
      } as IOrderToShipping;

      setOrderToShipping(orderToShipping);
    });

    getOrderPackages(orderNumber)
      .then((orderPackages) => {
        setOrderPackages(orderPackages);
        setOrderPackageShipping({ ...orderPackageShipping, orderId: orderPackages.orderNumber });
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  const handleGetOrders = () => {
    setIsLoading(true);
    getOrders(pagination)
      .then((ordersResponse) => {
        if (ordersResponse) {
          setOrders(ordersResponse.orders);
          setTotalOrders(ordersResponse.totalCount);
        } else {
          setOrders([]);
          setTotalOrders(0);
        }
      })
      .catch(() => {
        // TODO redireccionar en 403
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  const options: MUIDataTableOptions = {
    print: false,
    filter: false,
    count: totalOrders,
    sort: true,
    serverSide: true,
    search: true,
    viewColumns: false,
    download: false,
    rowHover: true,
    rowsPerPageOptions: [10, 20, 100],
    textLabels,
    selectableRows: 'multiple',
    onRowSelectionChange: (_: any[], allRowsSelected: any[]) => {
      let generatePdfSelected: IOrderId = {
        orderId: 0,
        trackingNumbers: []
      };
      const generatePdfArray: IOrderId[] = [];
      allRowsSelected.map((item: any) => {
        const orderSelected = orders[item.index];
        let productTrackingNumber: string[] = [];

        orderSelected.products
          .filter((product: OrderProducts) => product.trackingNumbers)
          .map((product: OrderProducts) => {
            if (typeof product.trackingNumbers === 'string') {
              productTrackingNumber.push(product.trackingNumbers);
            }
            if (Array.isArray(product.trackingNumbers)) {
              productTrackingNumber = productTrackingNumber.concat(product.trackingNumbers);
            }
          });

        generatePdfSelected = {
          orderId: orderSelected.orderNumber,
          trackingNumbers: productTrackingNumber
        };

        generatePdfArray.push(generatePdfSelected);
      });
      setGeneratePdfInfo({
        ...generatePdfInfo,
        ordersToPdf: generatePdfArray
      });
    },
    onTableChange(action: string, tableState: MUIDataTableState) {
      if (action === 'changePage') {
        const newPagination: PaginationOrders = { ...pagination, page: tableState.page + 1 };
        setPagination(newPagination);
      }
      if (action === 'changeRowsPerPage') {
        const newPagination: PaginationOrders = { ...pagination, limit: tableState.rowsPerPage };
        setPagination(newPagination);
      }
      if (action === 'sort') {
        const newPagination: PaginationOrders = {
          ...pagination,
          orderBy: tableState.sortOrder.name,
          order: tableState.sortOrder.direction
        };
        setPagination(newPagination);
      }
      if (action === 'onSearchClose') {
        const newPagination: PaginationOrders = {
          ...pagination,
          search: '',
          page: tableState.page + 1
        };
        setPagination(newPagination);
      }
    },
    customToolbarSelect: () => {
      return (
        <>
          <Grid container direction="row" justify="flex-end" alignItems="center">
            <Grid item xs={12} sm={4}>
              <TextField
                name="labelId"
                fullWidth
                select
                margin="dense"
                variant="outlined"
                label="Formato del rótulo"
                defaultValue="55"
                SelectProps={{
                  native: true
                }}
                error={validateField.haveErrors}
                onChange={(e) => {
                  setLabelId(e.target.value);
                  if (e.target.value !== '') {
                    setValidateField({
                      haveErrors: false,
                      messageError: ''
                    });
                  }
                }}
                helperText={validateField?.messageError}
                InputLabelProps={{ shrink: true }}
              >
                <option aria-label="None" value="">
                  Selecciona formato del rótulo
                </option>
                <option aria-label="44" value="44">
                  Genérico
                </option>
                <option aria-label="55" value="55">
                  Genérico 10 x 10
                </option>
                <option aria-label="57" value="57">
                  Genérico tercio de página
                </option>
                <option aria-label="58" value="58">
                  Genérico media página
                </option>
                <option aria-label="59" value="59">
                  Genérico cuarto de página
                </option>
              </TextField>
            </Grid>
            <Tooltip title="Descargar">
              <IconButton
                color="inherit"
                onClick={() => {
                  if (labelId === '') {
                    setValidateField({
                      haveErrors: true,
                      messageError: 'Campo obligatorio'
                    });
                    return;
                  }
                  setIsLoading(true);
                  generatePdfOfShippings(generatePdfInfo, labelId).finally(() => {
                    setIsLoading(false);
                  });
                }}
                style={{ margin: '0 15px' }}
              >
                <CloudDownloadIcon />
                {isLoading ? <CircularProgress size={24} style={{ marginLeft: 10 }} /> : ''}
              </IconButton>
            </Tooltip>
          </Grid>
        </>
      );
    },
    isRowSelectable: (dataIndex: number) => {
      const order = orders[dataIndex];

      const metaTrackingNumber = order?.products.find((product: any) => product?.trackingNumbers?.length > 0);
      if (metaTrackingNumber) return true;
      return false;
    },
    customSearchRender: (_searchText: string, handleSearch: any, hideSearch: any, _options: any) => {
      return (
        <CustomToolBarOrders
          pagination={pagination}
          setPagination={setPagination}
          handleSearch={handleSearch}
          hideSearch={hideSearch}
        />
      );
    }
  };

  const columns: MUIDataTableColumnDef[] = [
    // 1 id -> Pedido
    {
      name: 'orderNumber',
      label: 'Pedido'
    },

    // 2 status -> Estado del pedido
    {
      name: 'status',
      label: 'Estado del pedido',
      options: {
        customBodyRender: (value) => {
          switch (value) {
            case 'processing':
              return 'Procesando';
            case 'enviado':
              return 'Enviado';
            case 'envio-parcial':
              return 'Enviado parcial';
            default:
              return value;
          }
        }
      }
    },
    // 3 date_created -> Fecha del pedido
    {
      name: 'createdAt',
      label: 'Fecha del pedido',
      options: {
        customBodyRender: (value) => {
          return format(
            parse(value.substring(0, 19).replace('T', ' '), 'yyyy-MM-dd HH:mm:ss', new Date()),
            "dd MMMM 'a las' HH:mm",
            {
              locale: localeEs
            }
          );
        }
      }
    },
    // 4 meta_data -> Guía
    {
      name: 'products',
      label: 'Guía',
      options: {
        customBodyRender: (products: IOrderProducts[], tableMeta: MUIDataTableMeta) => {
          let isAllProductsHaveTrackingNumber = true;
          const arrayTrackingDetail: TrackingDetails[] = [];

          products.map((product) => {
            if (Array.isArray(product.trackingNumbers)) {
              product.trackingNumbers.map((t, trackingNumberIndex) => {
                const isArrayTrackingDetail = arrayTrackingDetail.find(
                  (trackingDetail) => trackingDetail.trackingNumber === t
                );

                if (isArrayTrackingDetail) {
                  return;
                }

                const trackingUrl = Array.isArray(product.trackingUrls)
                  ? product.trackingUrls[trackingNumberIndex]
                  : product.trackingUrls;

                arrayTrackingDetail.push({
                  trackingUrl: trackingUrl,
                  trackingNumber: t
                });
              });
            } else {
              const isArrayTrackingDetail = arrayTrackingDetail.find(
                (trackingDetail) => trackingDetail.trackingNumber === product.trackingNumbers
              );

              if (!isArrayTrackingDetail) {
                arrayTrackingDetail.push({
                  trackingUrl: product.trackingUrls as string,
                  trackingNumber: product.trackingNumbers
                });
              }
            }

            if (product.quantitiesShipped !== product.quantity) {
              isAllProductsHaveTrackingNumber = false;
            }
          });

          return (
            <Grid container justify="flex-start" alignItems="center">
              {arrayTrackingDetail.map((trackingDetail: TrackingDetails) => {
                if (trackingDetail.trackingNumber) {
                  return (
                    <Grid container justify="flex-start" alignItems="center" key={trackingDetail.trackingNumber}>
                      <Chip
                        variant="outlined"
                        size="small"
                        color="primary"
                        label={`${trackingDetail.trackingNumber}`}
                        clickable
                        style={{ marginBottom: 10 }}
                        onClick={() => {
                          if (trackingDetail.trackingNumber && trackingDetail.trackingNumber.includes('QC')) {
                            return false;
                          } else {
                            window.open(`${trackingDetail.trackingUrl}`);
                          }
                        }}
                      />
                    </Grid>
                  );
                }
              })}
              {!isAllProductsHaveTrackingNumber &&
                tableMeta.rowData[1].toLowerCase() !== 'enviado' &&
                differenceInDays(new Date(), new Date(tableMeta.rowData[2])) <= 31 && (
                  <Grid container justify="flex-start" alignItems="center">
                    <Button
                      variant="outlined"
                      size="small"
                      disabled={isLoading}
                      color="primary"
                      onClick={async () => {
                        createShipping(tableMeta.rowData[0]);
                      }}
                    >
                      {isLoading ? <CircularProgress size={24} /> : ''} Crear
                    </Button>
                  </Grid>
                )}
            </Grid>
          );
        }
      }
    }
  ];

  useEffect(() => {
    handleGetOrders();
  }, [pagination]);

  return (
    <AdminTemplate>
      <Grid container justify="center" spacing={2}>
        <Grid item xs={12} sm={12}>
          <Typography variant="h5" component="h1" align="center">
            Generación de guías
          </Typography>
        </Grid>
        <Grid item xs={12} sm={12}>
          <MuiTable title="Lista de pedidos" isLoading={isLoading} columns={columns} data={orders} options={options} />
        </Grid>
      </Grid>
      <CreateShippingModal getOrders={handleGetOrders} />
    </AdminTemplate>
  );
}
